(() => {
  const safeWebUrl = (value) => {
    if (!/^(?:https?:\/\/|www\.)/i.test(value) || /[\s<>"'`\\\u0000-\u001f\u007f]/.test(value)) return null;
    try {
      const url = new URL(/^www\./i.test(value) ? `https://${value}` : value);
      if (!['http:', 'https:'].includes(url.protocol) || !url.hostname || url.username || url.password) return null;
      return url.href;
    } catch {
      return null;
    }
  };

  const appendAnswer = (bubble, text) => {
    // Build text nodes and validated anchors only; never interpret model output as HTML.
    const tokens = /!?\[([^\]\n]*)\]\(([^\s()]*(?:\([^\s()]*\)[^\s()]*)*)\)|(?:https?:\/\/|www\.)[^\s<>"'`\\\[\]【】「」“”‘’，。；！？、]+/gi;
    let cursor = 0;
    for (const match of text.matchAll(tokens)) {
      bubble.appendChild(document.createTextNode(text.slice(cursor, match.index)));
      const markdown = match[1] !== undefined;
      let destination = markdown ? match[2] : match[0];
      let suffix = '';
      if (!markdown) {
        while (/[.,;:!?)}\u3001\u3002\uff0c\uff1b\uff01\uff1f]$/.test(destination)) {
          const last = destination.slice(-1);
          if (last === ')' && destination.split('(').length >= destination.split(')').length) break;
          suffix = last + suffix;
          destination = destination.slice(0, -1);
        }
      }
      const href = safeWebUrl(destination);
      if (href && !match[0].startsWith('!')) {
        const link = document.createElement('a');
        link.href = href;
        link.textContent = markdown ? (match[1] || destination) : destination;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.className = 'ai-support-answer-link';
        link.style.textDecoration = 'underline';
        link.style.textUnderlineOffset = '3px';
        link.style.overflowWrap = 'anywhere';
        link.title = href;
        bubble.appendChild(link);
        bubble.appendChild(document.createTextNode(suffix));
      } else {
        bubble.appendChild(document.createTextNode(match[0]));
      }
      cursor = match.index + match[0].length;
    }
    bubble.appendChild(document.createTextNode(text.slice(cursor)));
  };

  const init = (section) => {
    if (!section || section.dataset.aiReady === 'true') return;
    section.dataset.aiReady = 'true';

    const endpoint = section.dataset.apiEndpoint;
    const fallbackLink = section.dataset.gptLink;
    const form = section.querySelector('[data-ai-form]');
    const messages = section.querySelector('[data-ai-messages]');
    const suggestions = section.querySelector('[data-ai-suggestions]');
    const input = section.querySelector('[data-ai-input]');
    const product = section.querySelector('[data-ai-product]');
    const vehicle = section.querySelector('[data-ai-vehicle]');
    const issue = section.querySelector('[data-ai-issue]');
    const submit = section.querySelector('[data-ai-submit]');
    const history = [];

    if (!form || !messages || !input || !submit) return;

    const appendMessage = (role, text, pending = false) => {
      const row = document.createElement('div');
      row.className = `ai-support-message ai-support-message--${role}`;
      if (pending) row.dataset.aiPending = 'true';

      const avatar = document.createElement('span');
      avatar.className = 'ai-support-message__avatar';
      avatar.setAttribute('aria-hidden', 'true');
      avatar.textContent = role === 'user' ? 'You' : 'AI';

      const bubble = document.createElement('div');
      bubble.className = 'ai-support-message__bubble';
      if (role === 'assistant' && !pending) appendAnswer(bubble, text);
      else bubble.textContent = text;
      row.append(avatar, bubble);
      messages.appendChild(row);
      messages.scrollTop = messages.scrollHeight;
      return row;
    };

    const appendFallback = () => {
      if (!fallbackLink) return;
      const link = document.createElement('a');
      link.className = 'ai-support-fallback';
      link.href = fallbackLink;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = 'Open the official OBDResource AI Support Center';
      messages.appendChild(link);
    };

    const ask = async () => {
      const question = [product?.value, vehicle?.value, issue?.value, input.value]
        .map((value) => (value || '').trim())
        .filter(Boolean)
        .join(' | ');

      if (!question) {
        appendMessage('assistant', 'Please enter your question first.');
        input.focus();
        return;
      }

      appendMessage('user', question);
      input.value = '';
      if (suggestions) suggestions.hidden = true;
      submit.disabled = true;
      submit.textContent = 'Thinking...';
      const pending = appendMessage('assistant', 'Thinking...', true);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: question, history: history.slice(-12) })
        });
        if (!response.ok) throw new Error('AI service unavailable');
        const data = await response.json();
        if (!data?.answer) throw new Error('Invalid response');
        pending.remove();
        appendMessage('assistant', data.answer);
        history.push({ role: 'user', content: question }, { role: 'assistant', content: data.answer });
        if (history.length > 12) history.splice(0, history.length - 12);
      } catch (error) {
        pending.remove();
        appendMessage('assistant', 'The embedded AI service is temporarily unavailable. Please use the official support center or contact our support team.');
        appendFallback();
      } finally {
        submit.disabled = false;
        submit.textContent = 'Ask AI';
        input.focus();
      }
    };

    section.querySelectorAll('[data-ai-question]').forEach((button) => {
      button.addEventListener('click', () => {
        input.value = button.dataset.aiQuestion || '';
        input.focus();
      });
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      ask();
    });

    form.addEventListener('keydown', (event) => {
      if (event.isComposing || event.key !== 'Enter' || (event.target === input && event.shiftKey)) return;
      event.preventDefault();
      if (!submit.disabled) ask();
    });
  };

  const initAll = (root = document) => root.querySelectorAll('[data-ai-support]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
